import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Star, Quote } from 'lucide-react';

export const TestimonialsSection = ({ content, onPostProjectClick }) => {
  const testimonials = content?.testimonials || [];

  // Don't render the section if there's no API data
  if (!content || testimonials.length === 0) return null;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Star size={16} />
            <span>{content.header?.badge || 'Customer Success Stories'}</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {content.header?.title?.split(' ').slice(0, -1).join(' ')}{' '}
            <span className="text-emerald-600">{content.header?.title?.split(' ').slice(-1)[0]}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {content.header?.subtitle}
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {testimonials.map((testimonial) => (
            <Card key={testimonial.id} className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 shadow-lg relative overflow-hidden">
              {/* Decorative Quote */}
              <div className="absolute -top-2 -right-2 bg-emerald-500 text-white p-3 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300">
                <Quote size={32} />
              </div>

              <CardContent className="p-8">
                {/* Rating */}
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-current" size={16} />
                  ))}
                </div>

                {/* Comment */}
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.comment}"
                </p>

                {/* Customer Info */}
                <div className="flex items-center space-x-4 mb-4">
                  {testimonial.avatar && (
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <div className="font-semibold text-gray-900">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {testimonial.location}
                    </div>
                  </div>
                </div>

                {/* Project Details */}
                <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-600">Project</div>
                    <div className="font-medium text-gray-900">
                      {testimonial.project}
                    </div>
                  </div>
                  {testimonial.savings && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                      Saved {testimonial.savings}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        {content.cta && (
          <div className="text-center">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-white relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-4 left-4 w-16 h-16 bg-white rounded-full blur-xl"></div>
                <div className="absolute bottom-4 right-4 w-20 h-20 bg-white rounded-full blur-xl"></div>
              </div>

              <div className="relative">
                <h3 className="text-3xl font-bold mb-4">
                  {content.cta.title}
                </h3>
                <p className="text-emerald-100 mb-8 max-w-2xl mx-auto text-lg">
                  {content.cta.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => {
                      if (onPostProjectClick) {
                        onPostProjectClick();
                      } else {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    className="bg-white text-emerald-600 hover:bg-gray-50 px-8 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105"
                  >
                    {content.cta.primary_button?.text || 'Start Saving Today'}
                  </button>
                  <button
                    onClick={() => {
                      const howItWorksSection = document.getElementById('how-it-works');
                      if (howItWorksSection) {
                        howItWorksSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-3 rounded-xl font-medium transition-all duration-200"
                  >
                    {content.cta.secondary_button?.text || 'Learn More About Our Process'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
